/**
 * Spatial relationships between two features — the engine behind Select By
 * Location.
 *
 * WHY THIS IS CLIENT-SIDE, LIKE THE REST
 * ArcGIS and GeoServer can both evaluate these predicates themselves, and for
 * a very large layer that would be the better answer. It is deliberately not
 * what happens here: a file-backed layer has no service to ask, so a
 * server-side path would mean two implementations with different edge-case
 * behaviour, and "why does Intersect find 41 features on the imported copy and
 * 43 on the published one" is a worse problem than a feature cap. One
 * implementation, one answer everywhere.
 *
 * What IS pushed to the service is the cheap half: the source layer's bounding
 * box, so a target layer is not downloaded in full when the question only
 * concerns one ward. See `loadFeatures` in the panel folder.
 *
 * WHAT IS EXACT AND WHAT IS NOT
 * The predicates are exact for the geometry they are given: ray casting for
 * containment, orientation tests for crossings, point-to-segment for distance.
 * Nothing here is a centroid approximation — a long pipeline is tested along
 * its whole length. They work on the plane, in degrees, with distance
 * converted through a local scale factor, so they are accurate over a city or
 * a district and would drift over a shape spanning a continent.
 */

import { pointInPolygon, positionsOf } from './spatialSelect';

/**
 * The relationships offered.
 *
 * Esri publishes about fifteen; these are the four that carry the work, and
 * each maps onto an exact test below. The others are mostly boundary-handling
 * variants of these — adding them would mean four more names for distinctions
 * most people never need to draw.
 */
export type SpatialRelationship = 'intersect' | 'withinDistance' | 'contains' | 'within';

export const RELATIONSHIPS: {
  id: SpatialRelationship;
  label: string;
  /** Written from the user's side, completing "select features that ___". */
  help: string;
}[] = [
  {
    id: 'intersect',
    label: 'Intersect',
    help: 'Overlap the selecting features at all, even by a fraction.',
  },
  {
    id: 'withinDistance',
    label: 'Are within a distance of',
    help: 'Fall within the search distance, measured from the nearest edge.',
  },
  {
    id: 'within',
    label: 'Are completely within',
    help: 'Lie entirely inside the selecting features, which must be areas.',
  },
  {
    id: 'contains',
    label: 'Completely contain',
    help: 'Entirely enclose the selecting features. The target must be areas.',
  },
];

export interface Bbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

type Position = [number, number];
type Segment = [Position, Position];

/**
 * A feature's geometry, taken apart once.
 *
 * Every predicate needs the same three views of a geometry — its vertices, its
 * edges, its rings — and comparing two layers asks for them thousands of
 * times. Rebuilding them per comparison is what turns a two-second query into
 * a two-minute one.
 */
export interface PreparedGeometry {
  bbox: Bbox | null;
  positions: Position[];
  segments: Segment[];
  /** Areal parts only: each entry is one polygon's rings, outer ring first. */
  polygons: number[][][][];
}

/* ── Taking a geometry apart ─────────────────────────────────────────────── */

/** Visits every path in a geometry: a line, or one ring of a polygon. */
function eachPath(
  geometry: GeoJSON.Geometry | null | undefined,
  visit: (path: number[][], closed: boolean) => void,
): void {
  const g = geometry as any;
  switch (g?.type) {
    case 'LineString':
      visit(g.coordinates, false);
      break;
    case 'MultiLineString':
      for (const line of g.coordinates) visit(line, false);
      break;
    case 'Polygon':
      for (const ring of g.coordinates) visit(ring, true);
      break;
    case 'MultiPolygon':
      for (const polygon of g.coordinates) for (const ring of polygon) visit(ring, true);
      break;
    case 'GeometryCollection':
      for (const part of g.geometries ?? []) eachPath(part, visit);
      break;
    default:
      // Points have no edges. Nothing to visit, and that is not an error.
      break;
  }
}

function segmentsOf(geometry: GeoJSON.Geometry | null | undefined): Segment[] {
  const out: Segment[] = [];
  eachPath(geometry, (path, closed) => {
    for (let i = 1; i < path.length; i += 1) {
      out.push([
        [path[i - 1][0], path[i - 1][1]],
        [path[i][0], path[i][1]],
      ]);
    }
    /* GeoJSON rings repeat their first position, but one written by hand may
       not. Closing it here costs nothing and stops a polygon from being tested
       as though its boundary had a gap in it. */
    const first = path[0];
    const last = path[path.length - 1];
    if (closed && path.length > 2 && (first[0] !== last[0] || first[1] !== last[1])) {
      out.push([
        [last[0], last[1]],
        [first[0], first[1]],
      ]);
    }
  });
  return out;
}

function polygonsOf(geometry: GeoJSON.Geometry | null | undefined): number[][][][] {
  const g = geometry as any;
  if (g?.type === 'Polygon') return [g.coordinates];
  if (g?.type === 'MultiPolygon') return g.coordinates;
  if (g?.type === 'GeometryCollection') {
    return (g.geometries ?? []).flatMap((part: any) => polygonsOf(part));
  }
  return [];
}

export function prepare(geometry: GeoJSON.Geometry | null | undefined): PreparedGeometry {
  const positions = positionsOf(geometry);
  return {
    bbox: bboxOfPositions(positions),
    positions,
    segments: segmentsOf(geometry),
    polygons: polygonsOf(geometry),
  };
}

/* ── Bounding boxes ──────────────────────────────────────────────────────── */

function bboxOfPositions(positions: Position[]): Bbox | null {
  if (positions.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of positions) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

export function bboxUnion(boxes: (Bbox | null)[]): Bbox | null {
  let out: Bbox | null = null;
  for (const box of boxes) {
    if (!box) continue;
    if (!out) {
      out = { ...box };
      continue;
    }
    out.minX = Math.min(out.minX, box.minX);
    out.minY = Math.min(out.minY, box.minY);
    out.maxX = Math.max(out.maxX, box.maxX);
    out.maxY = Math.max(out.maxY, box.maxY);
  }
  return out;
}

/** Grows a box by a margin in degrees, so a distance query still catches it. */
export function padBbox(box: Bbox, padX: number, padY: number): Bbox {
  return {
    minX: box.minX - padX,
    minY: box.minY - padY,
    maxX: box.maxX + padX,
    maxY: box.maxY + padY,
  };
}

function boxesOverlap(a: Bbox | null, b: Bbox | null, padX = 0, padY = 0): boolean {
  if (!a || !b) return false;
  return (
    a.minX - padX <= b.maxX &&
    a.maxX + padX >= b.minX &&
    a.minY - padY <= b.maxY &&
    a.maxY + padY >= b.minY
  );
}

function boxContains(outer: Bbox | null, inner: Bbox | null): boolean {
  if (!outer || !inner) return false;
  return (
    outer.minX <= inner.minX &&
    outer.minY <= inner.minY &&
    outer.maxX >= inner.maxX &&
    outer.maxY >= inner.maxY
  );
}

/* ── Distance ────────────────────────────────────────────────────────────── */

/** Metres in one degree of latitude. Longitude shrinks by cos(latitude). */
const METRES_PER_DEGREE = 111_319.49;

/**
 * Degrees spanning a given distance at a given latitude.
 *
 * Used both to pad a bounding box before the fine test and to widen the
 * request sent to a service.
 */
export function degreesForMetres(metres: number, atLatitude: number): { x: number; y: number } {
  const cosLat = Math.max(Math.cos((atLatitude * Math.PI) / 180), 1e-6);
  return {
    x: metres / (METRES_PER_DEGREE * cosLat),
    y: metres / METRES_PER_DEGREE,
  };
}

/**
 * Squared distance from a point to a segment, in metres.
 *
 * Longitude is scaled by cos(latitude) so a degree east and a degree north are
 * the same length before the arithmetic. Without it a "within 50 m" query
 * would reach a fifth further east than north at Delhi, and further still
 * closer to the poles.
 */
function pointToSegmentM2(p: Position, a: Position, b: Position, scaleX: number): number {
  const px = p[0] * scaleX;
  const py = p[1];
  const ax = a[0] * scaleX;
  const ay = a[1];
  const bx = b[0] * scaleX;
  const by = b[1];

  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;

  // A zero-length segment is a point; t = 0 falls through to that endpoint.
  let t = lengthSq === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const ex = (px - (ax + t * dx)) * METRES_PER_DEGREE;
  const ey = (py - (ay + t * dy)) * METRES_PER_DEGREE;
  return ex * ex + ey * ey;
}

function pointToPointM2(p: Position, q: Position, scaleX: number): number {
  const ex = (p[0] - q[0]) * scaleX * METRES_PER_DEGREE;
  const ey = (p[1] - q[1]) * METRES_PER_DEGREE;
  return ex * ex + ey * ey;
}

/** Nearest distance from a point to a whole geometry, squared, in metres. */
function pointToGeometryM2(
  p: Position,
  g: PreparedGeometry,
  scaleX: number,
  cutoff: number,
): number {
  let best = Infinity;
  if (g.segments.length > 0) {
    for (const [a, b] of g.segments) {
      const d2 = pointToSegmentM2(p, a, b, scaleX);
      if (d2 < best) best = d2;
      if (best <= cutoff) return best; // close enough; stop measuring
    }
    return best;
  }
  // A point layer has vertices but no edges.
  for (const q of g.positions) {
    const d2 = pointToPointM2(p, q, scaleX);
    if (d2 < best) best = d2;
    if (best <= cutoff) return best;
  }
  return best;
}

/**
 * Whether two geometries come within `metres` of each other.
 *
 * The nearest approach of two polylines is always at a vertex of one against a
 * segment of the other, so testing both directions finds the true minimum. The
 * vertex-to-vertex shortcut would miss two long parallel lines whose vertices
 * happen to be staggered.
 */
function withinDistance(
  a: PreparedGeometry,
  b: PreparedGeometry,
  metres: number,
  scaleX: number,
): boolean {
  const cutoff = metres * metres;
  for (const p of a.positions) {
    if (pointToGeometryM2(p, b, scaleX, cutoff) <= cutoff) return true;
  }
  for (const p of b.positions) {
    if (pointToGeometryM2(p, a, scaleX, cutoff) <= cutoff) return true;
  }
  return false;
}

/* ── Crossings ───────────────────────────────────────────────────────────── */

/** Cross product sign: which side of ab does c fall on? */
function orientation(a: Position, b: Position, c: Position): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

/** Is c — known to be collinear with ab — actually between a and b? */
function onSegment(a: Position, b: Position, c: Position): boolean {
  return (
    Math.min(a[0], b[0]) <= c[0] &&
    c[0] <= Math.max(a[0], b[0]) &&
    Math.min(a[1], b[1]) <= c[1] &&
    c[1] <= Math.max(a[1], b[1])
  );
}

/**
 * Do two segments meet at all, including merely touching at an endpoint?
 *
 * The inclusive test, and the right one for Intersect: two parcels sharing a
 * boundary do intersect, and someone asking which pipes touch a zone means the
 * ones ending exactly on its edge as well.
 */
function segmentsTouch(p1: Position, p2: Position, q1: Position, q2: Position): boolean {
  const o1 = orientation(p1, p2, q1);
  const o2 = orientation(p1, p2, q2);
  const o3 = orientation(q1, q2, p1);
  const o4 = orientation(q1, q2, p2);

  // The clean case: each segment straddles the other's line.
  if (o1 * o2 < 0 && o3 * o4 < 0) return true;

  /* Otherwise something is collinear or an endpoint sits on the other segment.
     Only a point actually ON the other segment counts — two collinear but
     disjoint segments must not be reported as touching. */
  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, p2, q2)) return true;
  if (o3 === 0 && onSegment(q1, q2, p1)) return true;
  if (o4 === 0 && onSegment(q1, q2, p2)) return true;
  return false;
}

/**
 * Do two segments properly cross, passing through each other?
 *
 * The strict version, used for containment. A shape whose edge grazes the
 * boundary of the shape holding it is still inside it; one whose edge passes
 * through that boundary is not. Requiring a sign change on both tests draws
 * exactly that line, and is why "completely within" does not reject every
 * parcel that happens to sit flush against its ward edge.
 */
function segmentsCross(p1: Position, p2: Position, q1: Position, q2: Position): boolean {
  const o1 = orientation(p1, p2, q1);
  const o2 = orientation(p1, p2, q2);
  const o3 = orientation(q1, q2, p1);
  const o4 = orientation(q1, q2, p2);
  return o1 * o2 < 0 && o3 * o4 < 0;
}

function anyEdgesMeet(
  a: Segment[],
  b: Segment[],
  test: (p1: Position, p2: Position, q1: Position, q2: Position) => boolean,
): boolean {
  for (const [p1, p2] of a) {
    for (const [q1, q2] of b) {
      if (test(p1, p2, q1, q2)) return true;
    }
  }
  return false;
}

/* ── The predicates ──────────────────────────────────────────────────────── */

function inAnyPolygon(point: Position, polygons: number[][][][]): boolean {
  for (const rings of polygons) {
    if (pointInPolygon(point, rings)) return true;
  }
  return false;
}

/**
 * Any overlap at all.
 *
 * Three ways two shapes can meet, and all three are needed. A vertex of one
 * inside the other covers containment either way round. Crossing edges cover
 * what neither vertex test sees: two roads meeting at a junction have no
 * vertex inside anything, and a rectangle laid across another as a cross has
 * every corner outside its partner.
 */
function intersects(a: PreparedGeometry, b: PreparedGeometry): boolean {
  if (!boxesOverlap(a.bbox, b.bbox)) return false;
  if (a.polygons.length > 0 && b.positions.some((p) => inAnyPolygon(p, a.polygons))) return true;
  if (b.polygons.length > 0 && a.positions.some((p) => inAnyPolygon(p, b.polygons))) return true;
  return anyEdgesMeet(a.segments, b.segments, segmentsTouch);
}

/**
 * Does `outer` completely hold `inner`?
 *
 * Every vertex inside, and no edge of the inner shape passing through the
 * outer boundary. The second half is what makes this correct for a concave
 * outer shape: a bar laid across a C-shaped ward can have both ends inside it
 * while its middle crosses the open side.
 */
function covers(outer: PreparedGeometry, inner: PreparedGeometry): boolean {
  /* Only an area can contain something. A line "containing" a point is not a
     question this tool answers, and false is the honest reply. */
  if (outer.polygons.length === 0) return false;
  if (inner.positions.length === 0) return false;
  if (!boxContains(outer.bbox, inner.bbox)) return false;
  for (const p of inner.positions) {
    if (!inAnyPolygon(p, outer.polygons)) return false;
  }
  return !anyEdgesMeet(inner.segments, outer.segments, segmentsCross);
}

/**
 * Does the target feature stand in this relationship to the source feature?
 *
 * @param scaleX cos(latitude) for the area being worked in; see
 *               `scaleForLatitude`. Passed in rather than derived per call
 *               because it is the same for every pair in one query.
 */
export function relate(
  target: PreparedGeometry,
  source: PreparedGeometry,
  relationship: SpatialRelationship,
  distanceMetres: number,
  scaleX: number,
): boolean {
  switch (relationship) {
    case 'intersect':
      return intersects(target, source);
    case 'withinDistance':
      // Touching is a distance of zero, and is cheaper to establish than to
      // measure.
      if (intersects(target, source)) return true;
      return withinDistance(target, source, distanceMetres, scaleX);
    case 'within':
      return covers(source, target);
    case 'contains':
      return covers(target, source);
    default:
      return false;
  }
}

/** The longitude scale factor for a working area, from its bounding box. */
export function scaleForLatitude(box: Bbox | null): number {
  const latitude = box ? (box.minY + box.maxY) / 2 : 0;
  return Math.max(Math.cos((latitude * Math.PI) / 180), 1e-6);
}

/**
 * Is this pair worth the full test?
 *
 * A bounding-box overlap is four comparisons against geometry work that can
 * run to thousands, so this is what keeps a query over two large layers
 * finishing in a moment rather than a minute.
 */
export function worthTesting(
  target: PreparedGeometry,
  source: PreparedGeometry,
  relationship: SpatialRelationship,
  padX: number,
  padY: number,
): boolean {
  if (relationship === 'withinDistance') {
    return boxesOverlap(target.bbox, source.bbox, padX, padY);
  }
  return boxesOverlap(target.bbox, source.bbox);
}
